import { useIsFetching } from '@tanstack/react-query'
import classNames from 'classnames'
import { type FC, StrictMode, useCallback } from 'react'
import { globalQueryKeys } from '@opallabs/api'
import { type MenuOption } from '@opallabs/concepts'
import { useWorkspace } from '@opallabs/hooks'
import { Icon, SingleSelect, TextInput } from '@opallabs/lapidary'
import * as styles from './styles'
import { usePairingFormState, validateName } from './useFormState'

type LabelSetOption = MenuOption<string>

const ids = {
  field: `${styles.rootClass}-field`,
  labelSet: `${styles.rootClass}-label-set`,
} as const

const MaybeControlledSingleSelectRoot = ({
  children,
}: {
  children?: React.ReactNode
}) => {
  const { isReadonly, labelSet, labelSets, setLabelSetId } =
    usePairingFormState()
  const mappedOptions: LabelSetOption[] = labelSets.map(
    ({ id, name, pairedCustomFieldId }) => ({
      label: name,
      value: id,
      disabled: pairedCustomFieldId != null,
    })
  )

  const onChange = (selectedItem: LabelSetOption | null) => {
    setLabelSetId(selectedItem?.value ?? undefined)
  }

  const selectedOption = labelSet
    ? ({
        label: labelSet.name,
        value: labelSet.id,
      } satisfies LabelSetOption)
    : null

  if (isReadonly) {
    return (
      <StrictMode>
        <SingleSelect.ControlledRoot
          options={mappedOptions}
          selectedOption={selectedOption}
        >
          {children}
        </SingleSelect.ControlledRoot>
      </StrictMode>
    )
  }

  return (
    <StrictMode>
      <SingleSelect.Root
        aria-labelledby={ids.labelSet}
        initialSelection={selectedOption}
        onChange={onChange}
        options={mappedOptions}
      >
        {children}
      </SingleSelect.Root>
    </StrictMode>
  )
}

export const Titles: FC = () => {
  const {
    canChangeCustomFieldName,
    canChangeLabelSet,
    customField,
    customFieldName,
    existingFieldNames,
    isReadonly,
    setCustomFieldName,
  } = usePairingFormState()

  const { id: workspaceId } = useWorkspace()
  const isFetchingLabelSets =
    useIsFetching({
      queryKey: globalQueryKeys.labelSets(workspaceId),
    }) > 0

  const nameValidator = useCallback(
    (value: string) =>
      validateName(value, {
        existingFieldNames,
        originalName: customField.name,
      }),
    [existingFieldNames, customField.name]
  )

  return (
    <StrictMode>
      <div
        className={classNames(
          styles.classes.tableRoot,
          styles.classes.titleRow
        )}
        css={styles.tableRoot}
      >
        <div className={styles.classes.header}>
          <span className={styles.classes.columnTitle} id={ids.field}>
            Field
          </span>
          <span className={styles.classes.columnTitle} id={ids.labelSet}>
            Label Set
          </span>
        </div>

        <div className={styles.classes.row} css={styles.row}>
          {/* The `<TextInput>` component has the input and error message as top-level siblings; to avoid layout issues
                  `<TextInput>` must be wrapped in a containing element. */}
          <div className={styles.classes.textInput}>
            <TextInput
              additionalAriaLabels={{
                'aria-labelledby': ids.field,
              }}
              disabled={isReadonly || !canChangeCustomFieldName}
              value={customFieldName ?? ''}
              onChange={(value: string) => setCustomFieldName(value)}
              required={true}
              validator={nameValidator}
            />
          </div>

          <Icon
            ariaHidden={true}
            glyph="gem-link"
            height={styles.iconSize}
            width={styles.iconSize}
          />

          <MaybeControlledSingleSelectRoot>
            <SingleSelect.SearchableBody>
              <SingleSelect.Trigger
                disabled={
                  isReadonly || !canChangeLabelSet || isFetchingLabelSets
                }
              >
                <SingleSelect.TriggerText />
              </SingleSelect.Trigger>
              <SingleSelect.Menu>
                <SingleSelect.MenuItems />
              </SingleSelect.Menu>
            </SingleSelect.SearchableBody>
          </MaybeControlledSingleSelectRoot>
        </div>
      </div>
    </StrictMode>
  )
}
Titles.displayName = 'AdminConfiguration: Titles ← PairingForm ← CustomFields'
